import React, { useEffect, useState } from "react";
import { Card, Typography, Table, Spin, Empty, Tag } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import tenderDashboardApis from "../../api/tenderDashboard/tenderDashboardApis";

const { Title, Text } = Typography;

interface Evaluator {
  id: string;
  name: string;
  tendersAssigned: number;
  pendingEvaluations: number;
}

const TopEvaluatorsProductivity: React.FC = () => {
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgId = currentUser?.orgid;

  const [data, setData] = useState<Evaluator[] | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await tenderDashboardApis.getTopEvaluators({
        orgIds: String(orgId),
      });
      const rows: any[] = res?.data?.data || [];
      const mapped: Evaluator[] = rows.map((r) => ({
        id: String(r?.evaluatorId ?? ""),
        name: r?.evaluatorName ?? "",
        tendersAssigned: r?.tendersAssigned ?? 0,
        pendingEvaluations: r?.totalPendingSubmissions ?? 0,
      }));
      setData(mapped);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Tenders Assigned",
      dataIndex: "tendersAssigned",
      key: "tendersAssigned",
      align: "center" as const,
      render: (value: number) => {
        let color = "default";
        if (value > 10) color = "green";
        else if (value >= 6) color = "blue";
        return <Tag color={color}>{value}</Tag>;
      },
    },
    {
      title: "Pending Evaluations",
      dataIndex: "pendingEvaluations",
      key: "pendingEvaluations",
      align: "center" as const,
      render: (value: number) => {
        let color = "green";
        if (value >= 3) color = "red";
        else if (value >= 1) color = "orange";
        return <Tag color={color}>{value}</Tag>;
      },
    },
  ];

  return (
    <Card
      title={
        <div style={{ padding: "10px 0" }}>
          <Title level={5} style={{ marginBottom: 0, color: "#004293" }}>
            Top 5 Evaluators - Productivity
          </Title>
          <Text type="secondary" style={{ fontSize: 12, color: "#8094ae" }}>
            Evaluators with most assigned and pending evaluations
          </Text>
        </div>
      }
      extra={
        <ReloadOutlined onClick={fetchData} style={{ cursor: "pointer" }} />
      }
      style={{ width: "100%" }}
      bodyStyle={{ padding: "0 14px 12px 14px" }}
    >
      {loading ? (
        <div style={{ padding: "40px 0", textAlign: "center" }}>
          <Spin tip="Loading..." />
        </div>
      ) : data && data.length > 0 ? (
        <div style={{ padding: 10 }}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={false}
            bordered
            size="small"
            rowClassName={(_, index) => (index % 2 === 0 ? "striped-row" : "")}
          />
        </div>
      ) : (
        <Empty description="No evaluator data found" />
      )}
    </Card>
  );
};

export default TopEvaluatorsProductivity;
